import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import { CookieConsentService } from '@shared/services/cookie-consent.service';
import { IFrameService } from '@shared/services/iframe.service';
import { CookieService } from 'ngx-cookie-service';
import {
  LocalStorage,
  LocalStorageService,
  SessionStorageService
} from 'ngx-webstorage';

@Injectable({
  providedIn: 'root'
})
export class CommonEnvironmentsService {
  @LocalStorage() public token: string;

  public readonly IS_PRODUCTION: boolean = Boolean(environment.IS_PRODUCTION);
  public readonly VERSION: string = String(environment.VERSION);
  public readonly ENDPOINTS_SOCKET: string = String(
    environment.ENDPOINTS.SOCKET
  );

  public readonly ENDPOINTS_ASSETS: string = String(
    environment.ENDPOINTS.ASSETS
  );

  public readonly ENDPOINTS_APP: string = String(
    environment.ENDPOINTS.FRONT_END.APP
  );

  public readonly ENDPOINTS_REFERENCES: string = String(
    environment.ENDPOINTS.FRONT_END.REFERENCES
  );

  public APPS: any = [
    {
      src: `${this.ENDPOINTS_ASSETS}/images/vitay.io/logo-turquoise.svg`,
      label: 'feedback',
      url: this.getDomainUrl(environment.ENDPOINTS.FRONT_END.FEEDBACK)
    },
    {
      src: `${this.ENDPOINTS_ASSETS}/images/vitay.io/logo-green.svg`,
      label: 'references',
      url: this.getDomainUrl(environment.ENDPOINTS.FRONT_END.REFERENCES)
    },
    // {
    //   src: `${this.ENDPOINTS_ASSETS}/images/vitay.io/logo-purple.svg`,
    //   label: 'jobs',
    //   url: environment.ENDPOINTS.FRONT_END.JOBS
    // },
    {
      src: `${this.ENDPOINTS_ASSETS}/images/vitay.io/logo-blue.svg`,
      label: 'configuration',
      url: this.getDomainUrl(environment.ENDPOINTS.FRONT_END.CONFIGURATION)
    }
  ];

  constructor(
    private readonly _cookies: CookieService,
    private readonly _ls: LocalStorageService,
    private readonly _iframe: IFrameService,
    private readonly _ss: SessionStorageService,
    private readonly _cookieConsent: CookieConsentService
  ) {}

  public getToken(token?: string) {
    if (this._iframe.isIframe) {
      return this.token;
    }

    if (!!!token) {
      const href = window.location.href;
      token = 'candidate-token';

      if (href.indexOf(':4000') > -1 || href.indexOf('admin.') > -1) {
        token = 'admin-token';
      } else if (
        // Configuration
        href.indexOf(':4300') > -1 ||
        href.indexOf('configuration.') > -1 ||
        // Feedback
        href.indexOf(':4400') > -1 ||
        href.indexOf('feedback.') > -1 ||
        // References
        href.indexOf(':4500') > -1 ||
        href.indexOf('references.') > -1
      ) {
        token = 'recruiter-token';
      }
    }

    return this._cookies.get(token);
  }

  public setToken(label: string, token: string, isLax: boolean = true) {
    if (this._iframe.isIframe) {
      return (this.token = token);
    }

    const domain = this.cookieDomain(label, isLax);

    // Required for iframe
    if (label !== 'mfa') {
      this.token = token;
    }

    this._cookies.set(label, token, {
      expires: 15,
      path: '/',
      sameSite: isLax ? 'Lax' : 'Strict',
      // Not working with secure
      // secure: true,
      domain
    });
  }

  // The Domain attribute setToken writes each cookie with. Deleting a cookie
  // requires the very same attribute, so cleanStorage() reuses this — a
  // delete without it only clears host-only cookies and the token survives
  // (the long-standing "can't delete cross apps cookies" problem).
  private cookieDomain(label: string, isLax: boolean): string {
    const hostname = window.location.hostname;

    let domain = 'vitay.io';
    if (!isLax) {
      domain = hostname;
    } else if (hostname.indexOf('vitay.co.uk') > -1) {
      domain = 'vitay.co.uk';
    } else if (environment.ENVIRONMENT !== 'development' || label === 'mfa') {
      const splitted = hostname.split('.');

      if (
        hostname.indexOf('probitypeople') > -1 ||
        hostname.indexOf('mie') > -1
      ) {
        domain = `${splitted[splitted.length - 3]}.${
          splitted[splitted.length - 2]
        }.${splitted[splitted.length - 1]}`;
      } else {
        domain = `${splitted[splitted.length - 2]}.${
          splitted[splitted.length - 1]
        }`;
      }
    }

    return domain;
  }

  public getHostname(): string {
    // TODO: consider removing this logic, seems unecessary
    const index = environment.ENVIRONMENT === 'dev' ? 2 : 1;
    return window.location.hostname.split('.')[index] || 'vitay';
  }

  public cleanStorage() {
    // Consent is per-browser, not per-account: the wipe below must not
    // discard it, or the banner re-appears after every logout/token expiry.
    const restoreConsent = this._cookieConsent.preserve();

    this._ls.clear();

    this._ss.clear();
    // this._ss.clear('user');
    // this._ss.clear('hiringfirm');
    // this._ss.clear('referencerelationships');
    // this._ss.clear('settings');

    // Host-only cookies (no Domain attribute).
    this._cookies.deleteAll('/');

    // Cookies setToken wrote with an explicit parent Domain: a delete must
    // carry the same attribute or the browser leaves them untouched, and a
    // surviving expired token re-triggers the 401 wipe on every visit — a
    // permanent redirect loop no amount of "logging out" escapes. Cover
    // every domain setToken can have used on this hostname.
    const domains = new Set([
      this.cookieDomain('', true),
      this.cookieDomain('mfa', true),
      window.location.hostname
    ]);
    domains.forEach((domain) => this._cookies.deleteAll('/', domain));

    restoreConsent();
  }

  private getDomainUrl(url: string) {
    if (environment.ENVIRONMENT === 'production') {
      const hostname = window.location.hostname;
      const splitted = hostname.split('.');
      const domain = `${splitted[splitted.length - 2]}.${
        splitted[splitted.length - 1]
      }`;

      const isWhiteLabeled = ['vitay', 'mintzscreeningservices'].includes(
        splitted[splitted.length - 2]
      );

      if (isWhiteLabeled) {
        url = url.replace(/vitay.(io|ca)/gi, domain);
      }
    }

    return url;
  }
}
